import React from 'react';
import { usePageMeta } from '../utils/usePageMeta';
import { Eyebrow } from '../components/Eyebrow';
import { Button } from '../components/Button';

export const Nursery: React.FC = () => {
  usePageMeta({
    title: 'Coffee Nursery - Quality Seedlings | Lot 20 Coffee',
    description: 'High-quality coffee seedlings for sustainable production in Kenya. Supporting coffee farmers with the best planting material from our nursery operations.',
    canonicalPath: '/nursery'
  });

  return (
    <>
      {/* Header */}
      <section className="pt-3 pb-16 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Eyebrow className="mb-3">Operations</Eyebrow>
          <h1 className="font-display text-4xl md:text-5xl text-[#404040] mb-6 max-w-3xl">
            Coffee Nursery
          </h1>
          <p className="font-display text-lg text-[#6B6B6B] max-w-2xl leading-relaxed">
            Growing the next generation of coffee plants for our farms and partner farmers
            at Toro Coffee Farm in Trans-Nzoia County.
          </p>
        </div>
      </section>

      {/* Where We're Based */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-16 lg:gap-24 items-start">
            <div>
              <Eyebrow className="mb-3">Location</Eyebrow>
              <h2 className="font-display text-3xl md:text-4xl text-[#404040] mb-6">
                Where We're Based
              </h2>
              <p className="font-display text-[#6B6B6B] leading-relaxed mb-8">
                Our coffee nursery is at Toro Coffee Farm in Trans-Nzoia County. It's where we grow
                the next generation of coffee plants for our farms and partner farmers.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'Perfect location', desc: 'ideal climate and soil conditions' },
                  { title: 'Strategic partnerships', desc: 'working with county agriculture offices' },
                  { title: 'Quality control', desc: 'we keep variety selection in our hands' },
                ].map(item => (
                  <div key={item.title} className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 bg-[#404040] rounded-full mt-2.5 shrink-0" />
                    <p className="font-display text-[#6B6B6B]">
                      <span className="font-medium text-[#404040]">{item.title}</span> — {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="aspect-[4/5] bg-[#F0EDE8]" />
          </div>
        </div>
      </section>

      {/* What We Grow */}
      <section className="py-24 md:py-32 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <Eyebrow className="mb-3">Varieties</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl text-[#404040]">What We Grow</h2>
            <p className="font-display text-lg text-[#6B6B6B] max-w-2xl mt-4">
              We focus on Kenyan classics like Batian, SL28, SL34, and Ruiru 11 — plus specialty
              varieties like Gesha and Pacamara for farmers we contract to sell back to us.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { name: 'Batian', desc: 'High-yielding variety with excellent disease resistance and consistent quality.' },
              { name: 'SL28', desc: 'Legendary Kenyan variety known for exceptional cup quality and complex flavors.' },
              { name: 'SL34', desc: 'Hardy variety with excellent drought tolerance and distinctive flavor profile.' },
              { name: 'Ruiru 11', desc: 'Compact, disease-resistant variety perfect for intensive farming systems.' },
            ].map(variety => (
              <div key={variety.name} className="border border-[#E8E6E2] p-6">
                <h3 className="text-xl font-display text-[#404040] mb-3">{variety.name}</h3>
                <p className="font-display text-sm text-[#6B6B6B] leading-relaxed">{variety.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 border border-[#E8E6E2] p-8 max-w-2xl">
            <Eyebrow size="sm" className="mb-3">Specialty Varieties</Eyebrow>
            <p className="font-display text-[#6B6B6B] leading-relaxed">
              We also grow <span className="font-medium text-[#404040]">Gesha</span> and{' '}
              <span className="font-medium text-[#404040]">Pacamara</span> for farmers we contract
              to sell back to us — ensuring these premium varieties get the specialized care they deserve.
            </p>
          </div>
        </div>
      </section>

      {/* Progress */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <Eyebrow className="mb-3">Scale</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl text-[#404040]">Our Progress</h2>
            <p className="font-display text-lg text-[#6B6B6B] max-w-2xl mt-4">
              Right now we've planted about 5,000 seedlings, with a target of 300,000. The nursery
              will supply both our own farms and regional farming programs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-x-8 gap-y-10">
            {[
              { number: '5,000', label: 'Seedlings planted so far' },
              { number: '300,000', label: 'Target seedling capacity' },
              { number: 'Regional', label: 'Farming programs supported' },
            ].map(stat => (
              <div key={stat.label} className="border-l-2 border-[#E85C27]/30 pl-6">
                <p className="font-display text-4xl md:text-5xl text-[#404040] mb-2">{stat.number}</p>
                <p className="text-sm text-[#6B6B6B]">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="py-24 md:py-32 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <Eyebrow className="mb-3">Partners</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl text-[#404040] mb-6">
              Who We Work With
            </h2>
            <p className="font-display text-[#6B6B6B] leading-relaxed mb-8">
              We partner with county agriculture offices to get seedlings to farmers who need them,
              while keeping quality and variety selection in our hands.
            </p>
            <div className="space-y-3">
              {[
                'County agriculture offices',
                'Regional farming programs',
                'Individual farmers (direct orders)',
              ].map(partner => (
                <div key={partner} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-[#404040] rounded-full shrink-0" />
                  <span className="text-[#404040]">{partner}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#404040]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-6">
            <span className="text-brand-accent">Get seedlings —</span> looking to plant?
          </h2>
          <p className="font-display text-[#A3A3A3] leading-relaxed mb-10 max-w-xl mx-auto">
            If you're a farmer looking for seedlings or want to know more, get in touch
            and we'll be happy to help.
          </p>
          <Button to="/contact?subject=Nursery/Seedlings" variant="primary" size="lg">Request Seedlings</Button>
        </div>
      </section>
    </>
  );
};
